import * as $ from 'jquery';
import { openLink } from './open-link';
import { updateOfferClickId, getSessionId, getVisitId } from './tracker';
import { buildApiUrl } from './api-host';

/**
 * Инициализирует общую логику витрины: клик по офферу, чекер, анимации, пополнение.
 *
 * @param {object} opts
 * @param {string}   opts.clickSelector  - CSS-селектор кликабельного элемента оффера
 * @param {Function} opts.template       - (offer, num) => html string
 * @param {Function} opts.showPreloader  - (el) => void
 * @param {Function} opts.hidePreloader  - (el) => void
 */
export function initShowcaseCore({ clickSelector, template, showPreloader, hidePreloader }) {
  let productsWithoutCheckerCache = null;
  let productsWithoutCheckerRequest = null;
  const shownOfferIdsByNum = new Map();
  const replenishEnabled = Boolean($('[data-hide-on-click]').length && $('[data-replenish]').length);
  const checkerEnabled = Boolean($('[data-checker]').length && new URLSearchParams(window.location.search).get('ph'));
  let productsForReplenishCache = null;
  let productsForReplenishRequest = null;

  function initShownOfferIds() {
    $('[data-offers]').each(function () {
      const $container = $(this);
      const num = String($container.data('offers'));
      if (!num) return;

      let set = shownOfferIdsByNum.get(num);
      if (!set) {
        set = new Set();
        shownOfferIdsByNum.set(num, set);
      }

      $container.find('[data-offer-id]').each(function () {
        set.add(String($(this).data('offer-id')));
      });
    });
  }

  initShownOfferIds();

  function getLink() {
    $(document)
      .off('click', clickSelector)
      .on('click', clickSelector, function (event) {
        event.preventDefault();

        // Do not handle clicks for BankiRu cards here; they have their own flow
        // Detect by presence of data-product-code or data-product-id on the wrapper or any ancestor
        const $clicked = $(this);
        if (
          $clicked.is('[data-product-code], [data-product-id]') ||
          $clicked.closest('[data-product-code], [data-product-id]').length
        ) {
          return; // let BankiRu handler manage it
        }

        if ($(this).data('clickPending')) return;
        $(this).data('clickPending', true);

        const isBestOffer = $(this).is('[data-best-offer]');
        const offerId = $(this).data('offer-id');
        const offerVisitId = $(this).data('offer-visit-id');
        const $container = $(this).closest('[data-offers]');
        const offersNum = $container.data('offers');
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set(isBestOffer ? 'innerOfferId' : 'offerId', offerId);
        urlParams.set('sessionId', getSessionId() || '');
        urlParams.set('visitId', getVisitId() ?? '');
        urlParams.set('position', Array.from(document.querySelectorAll('[data-offer-id]')).indexOf(this) + 1);

        if (offerVisitId) {
          urlParams.set('offerVisitId', offerVisitId);
        }

        const clickSendBase = buildApiUrl('/api/click/send');
        const apiUrl = clickSendBase ? `${clickSendBase}?${urlParams.toString()}` : null;
        const currLink = this;

        showPreloader(currLink);

        function fallbackOpenFromProductUrl() {
          try {
            let baseLink = $(currLink).attr('data-offer-url') || $(currLink).data('offer-url');
            if (!baseLink) {
              throw new Error('Missing data-url on clicked offer element');
            }

            let url;
            try {
              url = new URL(baseLink, window.location.origin);
            } catch (_) {
              url = new URL(String(baseLink), window.location.origin);
            }

            const pageParams = new URLSearchParams(window.location.search);
            const utmContent = pageParams.get('utm_content');
            const utmCampaign = pageParams.get('utm_campaign');
            if (utmContent) url.searchParams.set('aff_sub1', utmContent);
            if (utmCampaign) url.searchParams.set('aff_sub2', utmCampaign);

            const affSub4 = pageParams.get('aff_sub4');
            if (affSub4) url.searchParams.set('aff_sub4', affSub4);

            const finalLink = url.toString();

            hidePreloader(currLink);
            $(currLink).data('clickPending', false);

            updateOfferClickId(null, finalLink);
            openLink(finalLink);

            removeOfferCardWithAnimation($(currLink)).then(() => {
              if (!replenishEnabled) return;
              ensureProductsWithoutChecker().then(() => {
                replenishOfferCard($container, offersNum, offerId);
              });
            });
          } catch (e) {
            hidePreloader(currLink);
            $(currLink).data('clickPending', false);
            console.error(e);
          }
        }

        // Offline mode or no API base: use data-offer-url and enrich with utm mappings
        if (window.__workOffline === true || !apiUrl) { fallbackOpenFromProductUrl(); return; }

        $.ajax({
          url: apiUrl,
          method: 'GET',
          timeout: 30000,
          success: function (response) {
            if (response.status === 'success' && response.link) {
              hidePreloader(currLink);
              $(currLink).data('clickPending', false);

              const affSub4 = new URLSearchParams(window.location.search).get('aff_sub4');
              let finalLink = response.link;

              if (affSub4) {
                try {
                  const url = new URL(finalLink, window.location.origin);
                  url.searchParams.set('aff_sub4', affSub4);
                  finalLink = url.toString();
                } catch (_) {}
              }

              updateOfferClickId(response.offerClickId || null, finalLink);
              openLink(finalLink);

              removeOfferCardWithAnimation($(currLink)).then(() => {
                if (!replenishEnabled) return;
                ensureProductsWithoutChecker().then(() => {
                  replenishOfferCard($container, offersNum, offerId);
                });
              });
            } else {
              // Invalid response: fallback to product URL like in offline mode
              fallbackOpenFromProductUrl();
            }
          },
          error: function (error) {
            // Request failed: fallback to product URL like in offline mode
            console.error(error);
            fallbackOpenFromProductUrl();
          },
        });
      });
  }

  function removeOfferCardWithAnimation($card) {
    const $container = $card.closest('[data-offers]');
    if (!$container.length || !$('[data-hide-on-click]').length) return Promise.resolve();

    const $cardsBefore = $container.find('[data-offer-id]');
    const firstPositions = getPositions($cardsBefore);

    $card.css('pointer-events', 'none');
    return animateRemove($card).then(() => {
      const $cardsAfter = $container.find('[data-offer-id]');
      return animateFLIP(firstPositions).then(() => {
        $cardsAfter.each(function () {
          this.style.transform = '';
        });
        $(document).trigger('offers:updated');
      });
    });
  }

  function replenishOfferCard($container, offersNum, removedOfferId) {
    if (!replenishEnabled) return;
    if (!$container?.length || !offersNum) return;
    if (!productsForReplenishCache?.data) return;

    const productsRaw = productsForReplenishCache.data[`products${offersNum}`] || [];
    const products = productsRaw.filter(o => o?.enabled == 1);
    if (!products.length) return;

    const limit = Number(productsForReplenishCache.data[`limit${offersNum}`]) || 0;
    if (limit && $container.find('[data-offer-id]').length >= limit) return;

    const key = String(offersNum);
    const shownSet = shownOfferIdsByNum.get(key) || new Set();
    shownOfferIdsByNum.set(key, shownSet);

    const nextOffer = products.find(o => !shownSet.has(String(o.id)));
    if (!nextOffer) return;

    shownSet.add(String(nextOffer.id));

    const $newCard = $(template(nextOffer, offersNum).trim());
    $container.append($newCard);
    $newCard.css({ opacity: 0, transform: 'scale(0.9)' });

    const el = $newCard[0];
    const animation = el.animate(
      [
        { opacity: 0, transform: 'scale(0.9)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: 500, easing: 'ease-out', fill: 'forwards' }
    );

    animation.onfinish = () => {
      el.style.opacity = '';
      el.style.transform = '';
    };
  }

  function getPositions($items) {
    const map = new Map();
    $items.each(function () {
      map.set(this, this.getBoundingClientRect());
    });
    return map;
  }

  function animateFLIP(firstPositions, duration = 500) {
    const items = [];

    firstPositions.forEach((firstRect, el) => {
      if (!document.body.contains(el)) return;

      const lastRect = el.getBoundingClientRect();
      const dx = firstRect.left - lastRect.left;
      const dy = firstRect.top - lastRect.top;

      if (!dx && !dy) return;
      items.push({ el, dx, dy });
    });

    if (!items.length) return Promise.resolve([]);

    items.forEach(({ el, dx, dy }) => {
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.willChange = 'transform';
    });

    void document.body.offsetHeight;

    const animations = items.map(({ el, dx, dy }) => {
      const animation = el.animate(
        [
          { transform: `translate(${dx}px, ${dy}px)` },
          { transform: 'translate(0, 0)' },
        ],
        { duration, easing: 'ease-out', fill: 'both' }
      );

      return animation.finished.finally(() => {
        el.style.willChange = '';
      });
    });

    return Promise.all(animations);
  }

  function animateRemove($el) {
    return new Promise(resolve => {
      const el = $el[0];

      el.animate(
        [
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(0.9)' },
        ],
        { duration: 500, easing: 'ease-in' }
      ).onfinish = () => {
        el.remove();
        resolve();
      };
    });
  }

  let checkerStartTime = null;
  const CHECKER_TIMEOUT_MS = 5 * 60 * 1000;
  let scoringRevealTimerId = null;
  const SCORING_REVEAL_MS = 10 * 1000;

  function readProductsBootstrap() {
    const el = document.getElementById('products-bootstrap');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  function filterProductsBootstrap(data, excludedMfo) {
    const excluded = new Set((excludedMfo || []).map(Number));
    const result = { ...(data || {}) };

    Object.keys(result).forEach(key => {
      if (!key.startsWith('products') || !Array.isArray(result[key])) return;
      result[key] = result[key].filter(o => o?.enabled == 1 && !excluded.has(Number(o.mfo)));
    });

    return result;
  }

  function getProductsWithChecker() {
    const urlParams = new URLSearchParams(window.location.search);
    const ph = urlParams.get('ph');
    const alg = urlParams.get('alg');

    if (window.__workOffline === true) return;

    if (!$('[data-checker]').length || !ph) return;

    if (!checkerStartTime) {
      checkerStartTime = Date.now();
    }

    if (!scoringRevealTimerId) {
      const $scoring = document.querySelector('.scoring');
      if ($scoring) {
        scoringRevealTimerId = window.setTimeout(() => {
          const $scoringCurrent = document.querySelector('.scoring');
          if ($scoringCurrent) $scoringCurrent.remove();
          scoringRevealTimerId = null;
        }, SCORING_REVEAL_MS);
      }
    }

    const visitId = window.__visitId || document.querySelector('[data-visit-id]')?.dataset.visitId || '';
    const checkerBase = buildApiUrl('/api/checker');
    if (!checkerBase) return;
    const apiUrl = `${checkerBase}?ph=${encodeURIComponent(ph)}&visitId=${encodeURIComponent(visitId)}${alg ? `&alg=${encodeURIComponent(alg)}` : ''}`;

    return $.ajax({
      url: apiUrl,
      method: 'GET',
      timeout: 30000,
      success: function (response) {
        const status = String(response.status).toLowerCase();

        if (status === 'wait') {
          const elapsed = Date.now() - checkerStartTime;
          if (elapsed >= CHECKER_TIMEOUT_MS) {
            console.log('Checker API timeout reached (5 minutes)');
            if (scoringRevealTimerId) {
              clearTimeout(scoringRevealTimerId);
              scoringRevealTimerId = null;
            }
            return;
          }
          setTimeout(() => getProductsWithChecker(), 2000);
          return;
        }

        if (status !== 'ok') return;

        const bootstrapData = readProductsBootstrap();
        response = {
          status: 'OK',
          data: filterProductsBootstrap(bootstrapData || {}, response.excludedMfo || []),
        };

        checkerStartTime = null;

        if (scoringRevealTimerId) {
          clearTimeout(scoringRevealTimerId);
          scoringRevealTimerId = null;
        }

        productsForReplenishCache = response;
        const updatePromises = [];

        $('[data-offers]')?.each(function () {
          const $container = $(this);
          const num = $container.data('offers');
          const productsRaw = response.data[`products${num}`] || [];

          const products = productsRaw.filter(o => o?.enabled == 1);
          const limit = response.data[`limit${num}`] || 0;
          const $cardsBefore = $container.find('[data-offer-id]');
          const firstPositions = getPositions($cardsBefore);
          const removePromises = [];

          $cardsBefore.each(function () {
            const $card = $(this);
            const offerId = String($card.data('offer-id'));
            const relevantOffer = products.find(o => o.id == offerId);

            if (relevantOffer) {
              relevantOffer.isRelevant = true;
              return;
            }

            $card.css('pointer-events', 'none');
            removePromises.push(animateRemove($card));
          });

          const updatePromise = Promise.all(removePromises).then(() => {
            const relevantCount = products.filter(o => o.isRelevant).length;
            let $newCards = $();

            if (limit > relevantCount) {
              const rest = products.filter(o => !o.isRelevant);
              const newCardsHtml = rest
                .slice(0, limit - relevantCount)
                .map(offer => template(offer, num).trim())
                .join('');

              $newCards = $(newCardsHtml);
              $container.append($newCards);
              $newCards.css({ opacity: 0, transform: 'scale(0.9)' });
            }

            const allCards = $container.find('[data-offer-id]');

            return new Promise(resolve => {
              requestAnimationFrame(() => {
                animateFLIP(firstPositions).then(() => {
                  allCards.each(function () {
                    this.style.transform = '';
                  });
                });

                $newCards.each(function () {
                  const el = this;
                  const animation = el.animate(
                    [
                      { opacity: 0, transform: 'scale(0.9)' },
                      { opacity: 1, transform: 'scale(1)' },
                    ],
                    { duration: 500, easing: 'ease-out', fill: 'forwards' }
                  );

                  animation.onfinish = () => {
                    el.style.opacity = '';
                    el.style.transform = '';
                  };
                });

                resolve();
              });
            });
          });

          updatePromises.push(updatePromise);
        });

        Promise.all(updatePromises)
          .then(() => {
            const animations = [];
            document.querySelectorAll('[data-offer-id]').forEach(el => {
              const anim = el.getAnimations();
              if (anim.length) animations.push(...anim.map(a => a.finished));
            });
            return Promise.all(animations);
          })
          .finally(() => {
            initShownOfferIds();
            const $scoring = document.querySelector('.scoring');
            if ($scoring) $scoring.remove();
          });
      },
      error: function (error) {
        console.log(error);
        checkerStartTime = null;
        if (scoringRevealTimerId) {
          clearTimeout(scoringRevealTimerId);
          scoringRevealTimerId = null;
        }
        const $scoring = document.querySelector('.scoring');
        if ($scoring) $scoring.remove();
      },
    });
  }

  function getProducts() {
    if (productsWithoutCheckerCache) return Promise.resolve(productsWithoutCheckerCache);

    const bootstrapData = readProductsBootstrap();
    if (!bootstrapData) return Promise.resolve(null);

    productsWithoutCheckerCache = { status: 'OK', data: bootstrapData };

    if (!checkerEnabled) {
      productsForReplenishCache = productsWithoutCheckerCache;
    }

    return Promise.resolve(productsWithoutCheckerCache);
  }

  function ensureProductsWithoutChecker() {
    if (!replenishEnabled) return Promise.resolve(null);
    if (productsForReplenishCache) return Promise.resolve(productsForReplenishCache);
    if (productsForReplenishRequest) return productsForReplenishRequest;

    if (checkerEnabled) {
      productsForReplenishRequest = Promise.resolve(getProductsWithChecker()).finally(() => {
        productsForReplenishRequest = null;
      });
      return productsForReplenishRequest;
    }

    if (productsWithoutCheckerCache) return Promise.resolve(productsWithoutCheckerCache);
    if (productsWithoutCheckerRequest) return productsWithoutCheckerRequest;

    productsWithoutCheckerRequest = Promise.resolve(getProducts()).finally(() => {
      productsWithoutCheckerRequest = null;
    });

    productsForReplenishRequest = productsWithoutCheckerRequest.then(() => productsForReplenishCache);
    productsForReplenishRequest.finally(() => {
      productsForReplenishRequest = null;
    });

    return productsForReplenishRequest;
  }

  getLink();
  initShownOfferIds();

  if (checkerEnabled) {
    productsForReplenishRequest = Promise.resolve(getProductsWithChecker()).finally(() => {
      productsForReplenishRequest = null;
    });
  }

  if (replenishEnabled && !checkerEnabled) {
    ensureProductsWithoutChecker();
  }
}

export function initBackRedirect() {
  const backUrlEl = document.querySelector('[data-backurl]');
  let backUrl = backUrlEl?.getAttribute('data-backurl');
  if (!backUrl) return console.log('[BackRedirect] Нет data-backurl');

  const aliasEl = document.querySelector('[data-alias]');
  const alias = aliasEl?.getAttribute('data-alias')?.trim();
  const passParamsEl = document.querySelector('[data-pass-params]');
  const passParams = passParamsEl?.hasAttribute('data-pass-params');

  let hijackDone = false;

  function hijackHistory() {
    if (hijackDone) return;
    hijackDone = true;
    history.replaceState({ step: 'intermediate' }, '', location.href);
    history.pushState({ step: 'step1' }, '', location.href);
    history.pushState({ step: 'step2' }, '', location.href);

    window.addEventListener('popstate', () => {
      let finalBackUrl = !/^https?:\/\//i.test(backUrl) ? ('https://' + backUrl) : backUrl;

      try {
        const currentUrl = new URL(window.location.href);
        const targetUrl = new URL(finalBackUrl);

        if (passParams) {
          currentUrl.searchParams.forEach((value, key) => {
            targetUrl.searchParams.set(key, value);
          });
        }

        if (alias) {
          targetUrl.searchParams.set('aff_sub4', alias);
        }

        finalBackUrl = targetUrl.toString();
      } catch (e) {
        console.log('[BackRedirect] Ошибка работы с URL', e);
      }

      try {
        const a = document.createElement('a');
        a.setAttribute('href', finalBackUrl);
        a.setAttribute('rel', 'noreferrer');
        a.setAttribute('target', '_self');
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (_) {
        window.location.href = finalBackUrl;
      }
    });
  }

  window.addEventListener('load', () => {
    document.addEventListener('click', hijackHistory, { once: true });
  });
}
